/**
 * NewCodeModal - Create/Edit Behavioral Code
 *
 * Admin-only modal for managing the behavioral code registry.
 * Supports create (POST) and edit (PUT) modes.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Typography,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { FAANA_DIMENSIONS, VALENCE_COLORS } from '../../constants/workbenchConfig';
import * as codeService from '../../services/behavioralCodeService';

const VALENCE_OPTIONS = [
  { value: 'positive', label: 'Positive' },
  { value: 'negative', label: 'Negative' },
  { value: 'neutral', label: 'Neutral' },
];

const EMPTY_FORM = {
  displayName: '',
  code: '',
  description: '',
  valence: 'positive',
  primaryDimension: '',
  secondaryDimensions: [],
  shortcut: '',
  examples: [],
};

function toCodeName(displayName) {
  return displayName
    .toUpperCase()
    .replace(/[^A-Z0-9\s]/g, '')
    .replace(/\s+/g, '_')
    .replace(/^_+|_+$/g, '');
}

// eslint-disable-next-line max-lines-per-function
function NewCodeModal({ open, onClose, onSaved, editCode }) {
  const isEdit = Boolean(editCode);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [autoCode, setAutoCode] = useState(true);

  // Populate form when editing
  useEffect(() => {
    if (editCode) {
      setForm({
        displayName: editCode.displayName || '',
        code: editCode.code || '',
        description: editCode.description || '',
        valence: editCode.valence || 'positive',
        primaryDimension: editCode.primaryDimension || '',
        secondaryDimensions: editCode.secondaryDimensions || [],
        shortcut: editCode.shortcut || '',
        examples: editCode.examples || [],
      });
      setAutoCode(false);
    } else {
      setForm(EMPTY_FORM);
      setAutoCode(true);
    }
    setError(null);
    setConfirmDelete(false);
  }, [editCode, open]);

  const handleChange = useCallback((field) => (event) => {
    const value = event.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'displayName' && autoCode) {
        next.code = toCodeName(value);
      }
      return next;
    });
  }, [autoCode]);

  const handleCodeChange = useCallback((e) => {
    setAutoCode(false);
    setForm((prev) => ({ ...prev, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '') }));
  }, []);

  const handleSecondaryDimChange = useCallback((e) => {
    setForm((prev) => ({ ...prev, secondaryDimensions: e.target.value }));
  }, []);

  const handleShortcutChange = useCallback((e) => {
    const val = e.target.value;
    setForm((prev) => ({ ...prev, shortcut: val.length > 0 ? val.charAt(val.length - 1).toUpperCase() : '' }));
  }, []);

  // Example management
  const addExample = useCallback(() => {
    setForm((prev) => ({
      ...prev,
      examples: [...prev.examples, { text: '', confidence: 0.9, rationale: '' }],
    }));
  }, []);

  const updateExample = useCallback((index, field, value) => {
    setForm((prev) => ({
      ...prev,
      examples: prev.examples.map((ex, i) =>
        i === index ? { ...ex, [field]: value } : ex
      ),
    }));
  }, []);

  const removeExample = useCallback((index) => {
    setForm((prev) => ({
      ...prev,
      examples: prev.examples.filter((_, i) => i !== index),
    }));
  }, []);

  const handleSave = async () => {
    setError(null);

    if (!form.displayName.trim() || !form.code.trim() || !form.description.trim() || !form.primaryDimension) {
      setError('Display name, code, description, and primary dimension are required.');
      return;
    }

    try {
      setSaving(true);
      if (isEdit) {
        await codeService.updateCode(editCode.id, form);
      } else {
        await codeService.createCode(form);
      }
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save code');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }

    try {
      setDeleting(true);
      await codeService.deleteCode(editCode.id);
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to delete code');
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const isValid = form.displayName.trim() && form.code.trim() && form.description.trim() && form.primaryDimension;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>
        {isEdit ? 'Edit Behavioral Code' : 'New Behavioral Code'}
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 0 }}>
          {/* Display Name */}
          <Grid size={12}>
            <TextField
              fullWidth
              label="Display Name"
              value={form.displayName}
              onChange={handleChange('displayName')}
              required
              size="small"
              placeholder="e.g. Ownership of Impact"
            />
          </Grid>

          {/* Code (auto-generated) */}
          <Grid size={8}>
            <TextField
              fullWidth
              label="Code"
              value={form.code}
              onChange={handleCodeChange}
              required
              size="small"
              helperText={autoCode ? 'Auto-generated from display name' : 'Custom code'}
              InputProps={{
                sx: { fontFamily: 'monospace', fontSize: '0.85rem' },
              }}
            />
          </Grid>

          {/* Shortcut */}
          <Grid size={4}>
            <TextField
              fullWidth
              label="Shortcut Key"
              value={form.shortcut}
              onChange={handleShortcutChange}
              size="small"
              helperText="Single character"
              inputProps={{ maxLength: 1 }}
            />
          </Grid>

          {/* Description */}
          <Grid size={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={form.description}
              onChange={handleChange('description')}
              required
              size="small"
              placeholder="Describe what this behavioral code measures..."
            />
          </Grid>

          {/* Valence */}
          <Grid size={6}>
            <TextField
              fullWidth
              select
              label="Valence"
              value={form.valence}
              onChange={handleChange('valence')}
              required
              size="small"
            >
              {VALENCE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: VALENCE_COLORS[opt.value],
                      }}
                    />
                    {opt.label}
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Primary Dimension */}
          <Grid size={6}>
            <TextField
              fullWidth
              select
              label="Primary Dimension"
              value={form.primaryDimension}
              onChange={handleChange('primaryDimension')}
              required
              size="small"
            >
              {FAANA_DIMENSIONS.map((dim) => (
                <MenuItem key={dim} value={dim}>{dim}</MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Secondary Dimensions */}
          <Grid size={12}>
            <TextField
              fullWidth
              select
              label="Secondary Dimensions"
              value={form.secondaryDimensions}
              onChange={handleSecondaryDimChange}
              size="small"
              SelectProps={{ multiple: true }}
              helperText="Optional"
            >
              {FAANA_DIMENSIONS.filter((d) => d !== form.primaryDimension).map((dim) => (
                <MenuItem key={dim} value={dim}>{dim}</MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Examples Section */}
          <Grid size={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Examples ({form.examples.length})
              </Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={addExample}>
                Add Example
              </Button>
            </Box>
            {form.examples.map((example, index) => (
              <Paper key={index} elevation={0} sx={{ p: 1.5, mb: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Example {index + 1}
                  </Typography>
                  <IconButton size="small" onClick={() => removeExample(index)}>
                    <DeleteIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  label="Text"
                  value={example.text}
                  onChange={(e) => updateExample(index, 'text', e.target.value)}
                  sx={{ mb: 1 }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Rationale"
                  value={example.rationale}
                  onChange={(e) => updateExample(index, 'rationale', e.target.value)}
                />
              </Paper>
            ))}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Box>
          {isEdit && (
            <Button
              color="error"
              onClick={handleDelete}
              disabled={deleting || saving}
              startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
            >
              {confirmDelete ? 'Confirm Delete' : 'Delete'}
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} disabled={saving || deleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!isValid || saving || deleting}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {isEdit ? 'Update' : 'Create'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default NewCodeModal;
